import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Stagger, StaggerItem } from "./motion";
import { IMAGES } from "./data";

const CREDENTIALS = [
  { year: "2022", text: "PhD in Clinical Psychology — Trinity College Dublin" },
  { year: "2017", text: "MSc in Applied Behaviour Analysis — NUI Galway" },
  { year: "2015", text: "Higher Diploma in Psychology (I.I) — NUI Galway" },
  { year: "—", text: "Chartered Senior Clinical Psychologist, PSI" },
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-6%", "10%"]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-40"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        {/* Portrait in clipped frame */}
        <div className="lg:col-span-5">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] prism-aura opacity-70 blur-lg -z-10" />
              <div className="overflow-hidden rounded-[2rem] border border-border p-2 bg-card">
                <div className="overflow-hidden rounded-[1.6rem] aspect-[4/5]">
                  <motion.img
                    style={{ y: yImg, scale: 1.08 }}
                    src={IMAGES.portrait}
                    alt="Dr. Bairbre Fee, Chartered Senior Clinical Psychologist"
                    className="h-full w-full object-cover"
                    data-testid="about-portrait"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Bio */}
        <div className="lg:col-span-7 lg:pt-6">
          <Reveal>
            <p
              className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.28em] mb-6"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              <span className="h-1.5 w-6 rounded-full prism-line" /> Meet your psychologist
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light tracking-tight leading-tight text-primary text-4xl sm:text-5xl lg:text-6xl">
              Dr. Bairbre Fee
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p
              className="mt-6 text-lg font-light leading-relaxed max-w-2xl"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              Bairbre is a Chartered Senior Clinical Psychologist based in South
              Dublin. Her work is grounded in behavioural science and delivered
              with real warmth — she believes people are best understood through
              their own unique lens, like light through a prism.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p
              className="mt-5 text-lg font-light leading-relaxed max-w-2xl"
              style={{ color: "hsl(var(--muted-foreground))" }}
            >
              She has a particular passion for supporting neurodivergent
              children and their families, alongside individual therapy and
              comprehensive psychological assessment for adults.
            </p>
          </Reveal>

          <Stagger className="mt-12 space-y-0 border-t border-border">
            {CREDENTIALS.map((c, i) => (
              <StaggerItem key={i}>
                <div
                  className="flex items-baseline gap-6 py-5 border-b border-border group"
                  data-testid={`credential-${i}`}
                >
                  <span className="font-serif text-2xl text-text-secondary w-16 shrink-0" style={{ color: "hsl(var(--muted-foreground))" }}>
                    {c.year}
                  </span>
                  <span className="text-base text-primary group-hover:translate-x-1 transition-transform duration-300">
                    {c.text}
                  </span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
};
